<template>
    <div class="vrm-scroll-fade">
        <div v-if="fadeEnabled" class="vrm-scroll-fade__shadows">
            <div ref="left" class="vrm-scroll-fade__shadows__left" />
            <div ref="right" class="vrm-scroll-fade__shadows__right" />
        </div>
        <div
            v-if="fadeEnabled"
            ref="scrollarea"
            class="vrm-scroll-fade__scrollarea"
            :class="customClasses"
            @scroll="updateIndicators"
        >
            <slot />
        </div>

        <div
            v-if="!fadeEnabled"
            ref="scrollarea"
            class="vrm-scroll-fade__scrollarea"
            :class="customClasses"
        >
            <slot />
        </div>
    </div>
</template>
<script>
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { AngularDirective } from '@/vue/decorators/angular-interop';
import { WindowEventListener } from '@/vue/decorators/window-event-listener';

@Component
@AngularDirective('vrm-scroll-fade')
class VrmScrollFade extends Vue {
    /** @type {Function} */
    updateIndicatorsFunc = null;
    /** @type {MutationObserver} */
    observer = null;

    @Prop({ type: Array, default: () => [] })
    customClasses;

    /**
     * Allows the fade + listeners to be disabled in specific situations for performance improvements.
     *
     * If there are a lot of scroll fades in a long list, performance suffers especially on mobile.
     */
    @Prop({ type: Boolean, default: () => true })
    fadeEnabled;

    created() {
        if (this.fadeEnabled) {
            this.updateIndicatorsFunc = this.updateIndicators.bind(this);
        }
    }

    async mounted() {
        if (this.fadeEnabled) {
            if (window.MutationObserver) {
                this.observer = new MutationObserver(this.updateIndicatorsFunc);
                this.observer.observe(this.$refs.scrollarea, {
                    childList: true,
                    subtree: true,
                });
            }
            // Also trigger an update to the indicator after a single update tick
            this.updateIndicators();
            await this.$nextTick();
            this.updateIndicators();
        }
    }

    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
            delete this.observer;
        }
    }

    @WindowEventListener('resize')
    updateIndicators() {
        // If the component is removed the frame after creation, the await nextTick in mounted continues after deletion
        // If fade is not enabled, never update indicators on resize.
        if (!this.$refs.scrollarea || !this.fadeEnabled) {
            return;
        }

        const content = this.$refs.scrollarea;
        const shadowLeft = this.$refs.left;
        const shadowRight = this.$refs.right;

        // To prevent shadows persisting due to decimal issues in the scrollLeft round values up and apply a small margin
        // to the 'is scrolled to end' check. Sometimes when scrolling to the end, the scrollLeft would fall 0.5 short
        // of the expected value.
        const scrollWidth = Math.ceil(content.scrollWidth);
        const offsetWidth = Math.ceil(content.offsetWidth);
        const scrollLeft = Math.ceil(content.scrollLeft);
        const scrollCheckMargin = 5;

        // If the total width of the content is larger than the layout determined width then shadows need to become visible.
        if (scrollWidth > offsetWidth) {
            content.classList.remove('vrm-scroll-fade__scrollarea--hidden-scrollbar');
            // If the area has scrolled right then the left shadow needs to be shown
            if (scrollLeft <= 0) {
                shadowLeft.style.display = 'none';
            } else {
                shadowLeft.style.display = 'block';
            }
            // If the right edge of the content is not aligned with the right edge of the visible portion of the content then the right shadow must appear
            // Note: scrollLeft becomes negative when scrolling right
            if (scrollWidth > offsetWidth + scrollLeft + scrollCheckMargin) {
                shadowRight.style.display = 'block';
            } else {
                shadowRight.style.display = 'none';
            }
        } else {
            content.classList.add('vrm-scroll-fade__scrollarea--hidden-scrollbar');
            shadowLeft.style.display = 'none';
            shadowRight.style.display = 'none';
        }
    }
}

export default VrmScrollFade;
</script>
